/**
 * AI-insight jobs (migration 0095) — a member asks a generated report (statistical / tag /
 * shift / six_sigma) for an honesty-first AI insight, scoped to that report's window + tags.
 * The platform makes zero AI calls: a PC "brain" (flat Claude Max) claims the job off the queue,
 * authors the report, and posts it as a report_run. The client enqueues, polls status/stage for
 * the progress indicator, can cancel, reads the monthly quota, and records thumbs feedback.
 * Mirrors internal/twin/insight_jobs.go.
 */

import { api } from "@/lib/api-client";

export type InsightReportKind = "statistical" | "tag" | "shift" | "six_sigma";

/** The scope the insight must respect. Shape varies by report kind (window always; tags for
 * tag/shift/six_sigma). Sent verbatim as the job's params. */
export interface InsightScope {
  window?: { from?: string; to?: string };
  tags?: string[];
  parts?: string[];
  granularity?: string;
  asof?: string;
  /** The deterministic report the user is looking at, attached so the AI insight cites the SAME
   *  numbers the report shows (not just the shared honesty facts). Opaque to the API; the brain
   *  reads it from the job params. Kept lean (aggregates, not raw heats) — see buildReportSnapshot. */
  report?: unknown;
}

export type InsightStage =
  | "queued" | "analysing" | "writing" | "verifying" | "posting" | "done" | "failed" | "cancelled";

/** One insight job. Mirrors twin.InsightJob. */
export interface InsightJob {
  id: string;
  report_kind: InsightReportKind;
  status: "pending" | "claimed" | "done" | "failed" | "cancelled";
  stage: InsightStage;
  progress: number;
  note?: string;
  attempts: number;
  report_run_id?: string | null;
  error?: string;
  created_at: string;
  heartbeat_at?: string | null;
  done_at?: string | null;
}

export interface InsightUsage {
  used: number;
  limit: number;
  remaining: number;
  resets_at: string;
}

export interface InsightEnqueueResult {
  job: InsightJob;
  usage: InsightUsage;
}

export type FeedbackVote = "up" | "down" | "";

export const insightJobs = {
  /** Enqueue an insight for a report scope. `POST /v1/twin/insight-jobs` → 201, or 429 when the
   *  monthly cap is reached (ApiClientError with status 429). */
  enqueue: (body: { report_kind: InsightReportKind; params: InsightScope }) =>
    api.post<InsightEnqueueResult>("/v1/twin/insight-jobs", body),
  /** Poll a job's status + stage. `GET /v1/twin/insight-jobs/{id}`. */
  get: (id: string) => api.get<InsightJob>(`/v1/twin/insight-jobs/${id}`),
  /** Cancel a pending / in-flight job (free — excluded from the quota). `POST …/{id}/cancel`. */
  cancel: (id: string) => api.post<void>(`/v1/twin/insight-jobs/${id}/cancel`),
  /** The org's monthly AI-insight quota. `GET /v1/twin/insight-jobs/usage`. */
  usage: () => api.get<InsightUsage>("/v1/twin/insight-jobs/usage"),
  /** Thumbs + comment on the produced report_run. `POST /v1/twin/report-runs/{id}/feedback`. */
  feedback: (runId: string, body: { vote?: FeedbackVote; comment?: string }) =>
    api.post<void>(`/v1/twin/report-runs/${runId}/feedback`, body),
};
